import { MemberStatus, Role } from '@prisma/client';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowData,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { ListMembersResponse, MemberWithUser } from '@zond/api-interfaces';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@zond/ui';
import { ArrowUpDown, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsers } from '../context/users-context';
import { UsersDeleteDialog } from './users-delete-dialog';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    className: string;
  }
}

interface UsersTableProps {
  users: MemberWithUser[];
  onDelete: (memberId: number) => void;
  onEdit: (member: MemberWithUser) => void;
  onInvite: () => void;
  isLoading?: boolean;
}

const getMemberStatusColor = (status: MemberStatus) => {
  switch (status) {
    case MemberStatus.Active:
      return 'default';
    case MemberStatus.Invited:
      return 'outline';
    case MemberStatus.Inactive:
      return 'secondary';
    default:
      return 'default';
  }
};

const UsersTable = ({ users, onDelete, onEdit, onInvite, isLoading }: UsersTableProps) => {
  const { t } = useTranslation('settings');
  const { setOpen, setCurrentRow } = useUsers();

  if (isLoading) {
    return (
      <div className="rounded-md border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('members.table.columns.username')}</TableHead>
              <TableHead>{t('members.table.columns.name')}</TableHead>
              <TableHead>{t('members.table.columns.email')}</TableHead>
              <TableHead>{t('members.table.columns.timezone')}</TableHead>
              <TableHead>{t('members.table.columns.status')}</TableHead>
              <TableHead>{t('members.table.columns.role')}</TableHead>
              <TableHead className="w-[70px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell colSpan={7}>
                <div className="flex justify-center py-6">{t('common:states.loading')}</div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <div className="flex items-center gap-1">
                  {t('members.table.columns.username')}
                  <ArrowUpDown className="h-4 w-4" />
                </div>
              </TableHead>
              <TableHead>{t('members.table.columns.name')}</TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  {t('members.table.columns.email')}
                  <ArrowUpDown className="h-4 w-4" />
                </div>
              </TableHead>
              <TableHead>{t('members.table.columns.timezone')}</TableHead>
              <TableHead>{t('members.table.columns.status')}</TableHead>
              <TableHead>{t('members.table.columns.role')}</TableHead>
              <TableHead className="w-[70px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!users?.length ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className="flex flex-col items-center justify-center py-6 gap-4">
                    <div className="text-center">
                      <h3 className="text-lg font-semibold">{t('members.table.empty.title')}</h3>
                      <p className="text-sm text-muted-foreground">{t('members.table.empty.description')}</p>
                    </div>
                    <Button onClick={onInvite}>
                      <Plus className="mr-2 h-4 w-4" />
                      {t('members.table.empty.invite')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              users.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={member.user?.avatar ?? undefined} />
                        <AvatarFallback>{member.user?.displayname?.[0] ?? '?'}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">
                        {member.status === MemberStatus.Invited ? (
                          <span className="text-muted-foreground">{t('members.table.pending')}</span>
                        ) : (
                          member.user?.displayname
                        )}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {member.status === MemberStatus.Invited ? (
                      <span className="text-muted-foreground">{t('members.table.pending')}</span>
                    ) : (
                      member.user?.displayname || '—'
                    )}
                  </TableCell>
                  <TableCell>{member.user?.email || member.email || '—'}</TableCell>
                  <TableCell>{member.user?.timezone || '—'}</TableCell>
                  <TableCell>
                    <Badge variant={getMemberStatusColor(member.status)}>{member.status}</Badge>
                  </TableCell>
                  <TableCell>{member.roles.map((role: Role) => role).join(', ')}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">{t('members.table.openMenu')}</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(member)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          {t('common:actions.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setCurrentRow(member);
                            setOpen('delete');
                          }}
                          className="text-red-500"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t('common:actions.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

export { UsersTable };
